// ./src/components/generic/TireTypeSelector/TireTypeSelector.tsx
import React, { useMemo } from "react";
import { Select, SelectProps, Group, Text, ThemeIcon } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TIRE_TYPE_OPTIONS, TIRE_TYPE_MAP } from "@/config/tireOptions";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TireTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Selected tire type value */
  value?: string | null;

  /** Callback when type changes */
  onChange?: (name: string | undefined, value: string | null | undefined) => void;
}

// ============================================
// Component
// ============================================

export const TireTypeSelector: React.FC<TireTypeSelectorProps> = ({
  name,
  value,
  onChange,
  placeholder = "Select tire type",
  label = "Tire Type",
  required = false,
  ...rest
}) => {
  const { t } = useTranslation(["tireSets"]);

  // Convert options to Select data format
  const options = useMemo(() => {
    return TIRE_TYPE_OPTIONS.map((opt) => ({
      value: '' + opt.value,
      label: t(`tireSets:${opt.labelKey}`),
    }));
  }, [t]);

  // Handle change with proper typing
  const handleChange = (newValue: string | null) => {
    onChange?.(name, newValue);
  };

  // Render option with icon
  const renderOption: SelectProps["renderOption"] = ({ option }) => {
    const tireType = TIRE_TYPE_MAP[option.value];
    if (!tireType) return option.label;

    return (
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon
          variant="light"
          color={tireType.color}
          size="sm"
          radius="xl"
        >
          <AppIcon iconName={tireType.icon || ''} size={14} />
        </ThemeIcon>
        <Text size="sm">{t(`tireSets:${tireType.labelKey}`)}</Text>
      </Group>
    );
  };

  return (
    <Select
      data={options}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      label={label}
      clearable={!required}
      withAsterisk={required}
      renderOption={renderOption}
      allowDeselect={false}
      maxDropdownHeight={300}
      {...rest}
    />
  );
};
